import { RowAction } from "@beeldit/core/enums/RowAction.enum";
import { TableAction } from "@beeldit/core/enums/TableAction.enum";
import { Check, Pencil, Plus, Prohibit, Trash } from "@phosphor-icons/react";
import { t } from "i18next";
import { Booking } from "./models/Booking";
import CustomerSelector from "@main/customers/components/CustomerSelector";
import PetCheckboxesSelector from "@main/pets/components/PetCheckboxesSelector";
import DaysInput from "./components/DaysInput";
import PricePerDaysInput from "./components/PricePerDaysInput";
import ExtraSelector from "@main/extras/components/ExtraSelector";
import RoomsSelector from "@main/rooms/components/RoomsSelector";
import TotalPriceInput from "./components/TotalPriceInput";
import PetSelector from "@main/pets/components/PetSelector";
import BeelditDateTimeInput from "@beeldit/core/components/BeelditDateTimeInput";
import AsyncCustomerSelector from "@main/customers/components/AsyncCustomerSelector";
import CustomerWithModalSelector from "@main/customers/components/CustomerWithModalSelector";

const bookingsCalendarConfig = {
  modelName: "booking",
  modelPluralName: "bookings",
  endpoint: "bookings",
  indexQueryParams: {
    relations: ["customer", "rooms", "pets"],
    cancelled: "false",
  },
  modelClass: Booking,
  modalFullScreen: true,
  tableConfig: {
    index: null,
    actions: [
      {
        name: TableAction.CREATE,
        label: "create",
        showLabel: true,
        allowed: false,
        show: false,
        action: null,
        icon: <Plus size={20} />,
      },
    ],
    rowActions: [
      {
        name: RowAction.EDIT,
        label: "edit",
        icon: <Pencil size={20} />,
        allowed: true,
        show: true,
        action: (element: any) => {
          console.log("edit", element);
        },
      },
      {
        name: RowAction.DELETE,
        label: "delete",
        icon: <Trash size={20} />,
        allowed: true,
        show: true,
        action: (element: any) => {
          console.log("delete", element);
        },
      },
      {
        name: "cancel",
        label: "cancel",
        icon: <Prohibit size={20} />,
        allowed: true,
        show: (element: any) => {
          return !element.cancelled;
        },
        action: (element: any) => {
          console.log("cancel", element);
        },
      },
      {
        name: "uncancel",
        label: "uncancel",
        icon: <Check size={20} />,
        allowed: true,
        show: (element: any) => {
          return element.cancelled;
        },
        action: (element: any) => {
          console.log("cancel", element);
        },
      },
    ],
    rowConfiguration: {
      class: (element: any) => {
        return null;
      },
    },
    columns: [
      {
        name: "id",
        label: "#",
        key: "id",
        type: "number",
        show: true,
        allowOrder: true,
      },
      {
        name: "cancelled",
        label: "cancelled",
        key: "cancelled",
        type: "boolean",
        show: true,
      },
      {
        name: "start_date_time",
        label: "start_date_time",
        key: "start_date_time",
        type: "datetime",
        show: true,
        allowOrder: true,
      },
      {
        name: "end_date_time",
        label: "end_date_time",
        key: "end_date_time",
        type: "datetime",
        show: true,
        allowOrder: true,
      },
      {
        name: "customer",
        label: "customer",
        key: "customer.full_name",
        type: "string",
        show: true,
        allowOrder: true,
      },
      {
        name: "rooms",
        label: "rooms",
        key: "rooms",
        attribute: "name",
        type: "array",
        show: true,
        allowOrder: true,
      },
      {
        name: "pets",
        label: "pets",
        key: "pets",
        attribute: "name",
        type: "array",
        show: true,
        allowOrder: true,
      },
      {
        name: "breeds",
        label: "breeds",
        key: "pets",
        attribute: "breed",
        type: "array",
        show: true,
        allowOrder: true,
      },
      {
        name: "payed",
        label: "payed",
        key: "payment.payed",
        type: "boolean",
        show: true,
        allowOrder: true,
      },
    ],
    pagination: {
      enabled: false,
      back: false,
    },
  },
  filterConfig: {
    type: "object",
    properties: {
      from: {
        type: "string",
        format: "date",
        title: t("from"),
        default: (() => {
          const now = new Date();
          const previousDate = new Date(now);
          previousDate.setDate(previousDate.getDate() - 3);
          return `${previousDate.getFullYear()}-${String(previousDate.getMonth() + 1).padStart(2, "0")}-${String(previousDate.getDate()).padStart(2, "0")}`;
        })(),
      },
      to: {
        type: "string",
        format: "date",
        title: t("to"),
        default: (() => {
          const now = new Date();
          const nextDate = new Date(now);
          nextDate.setMonth(nextDate.getMonth() + 1);
          return `${nextDate.getFullYear()}-${String(nextDate.getMonth() + 1).padStart(2, "0")}-${String(nextDate.getDate()).padStart(2, "0")}`;
        })(),
      },
    },
  },

  filterUIConfig: {
    uiScheme: {
      className: "filters-container",
      search: {
        "ui:options": {
          label: false, // Oculta el título del campo
          placeholder: "Buscar...",
        },
      },
    },
    widgets: {
      "customer-selector": CustomerSelector,
      "pet-selector": PetSelector,
    },
  },
  interactions: [
    {
      field: "pets",
      listen: "customer_id",
    },
  ],
  formSchema: {
    type: "object",
    properties: {
      // Reference
      reference: {
        type: "string",
        title: t("reference"),
        readonly: true,
      },
      // Customer
      customer_id: {
        type: "number",
        title: t("customer_id"),
      },
      // Pet
      pets: {
        type: "array",
        title: t("pets"),
        items: {
          type: "number",
        },
      },
      // Start date time
      start_date_time: {
        type: "string",
        format: "date-time",
        title: t("start_date_time"),
      },
      // End date time
      end_date_time: {
        type: "string",
        format: "date-time",
        title: t("end_date_time"),
      },
      // Rooms
      rooms: {
        type: "array",
        title: t("rooms"),
        items: {
          type: "number",
        },
      },
      // Days
      days: {
        type: "number",
        title: t("days"),
      },
      // Price per day
      price_per_day: {
        type: "number",
        title: t("price_per_day"),
        min: 0,
        step: 0.01,
        default: 0,
      },
      extras: {
        type: "array",
        title: t("extras"),
        items: {
          type: "object",
          properties: {
            extra_id: {
              type: "number",
              title: t("extra_id"),
            },
            price: {
              type: "number",
              title: t("extra_price"),
              min: 0,
              default: 0,
            },
            quantity: {
              type: "number",
              title: t("quantity"),
              min: 0,
              default: 1,
            },
          },
        },
      },
      total_price: {
        type: "number",
        title: t("total_price"),
        default: 0,
      },
      // Observation
      observation: {
        type: "string",
        title: t("observation"),
      },
    },
  },
  editFormSchema: {
    type: "object",
    properties: {
      // Reference
      reference: {
        type: "string",
        title: t("reference"),
        // readonly: true,
      },
      // Customer_indo
      customer_id: {
        type: "number",
        title: t("customer_id"),
      },

      // Pet
      pets: {
        type: "array",
        title: t("pets"),
        items: {
          type: "number",
        },
      },
      // Start date time
      start_date_time: {
        type: "string",
        format: "date-time",
        title: t("start_date_time"),
      },
      // End date time
      end_date_time: {
        type: "string",
        format: "date-time",
        title: t("end_date_time"),
      },
      // Rooms
      rooms: {
        type: "array",
        title: t("rooms"),
        items: {
          type: "number",
        },
      },
      // Days
      days: {
        type: "number",
        title: t("days"),
      },
      // Price per day
      price_per_day: {
        type: "number",
        title: t("price_per_day"),
        min: 0,
        step: 0.01,
        default: 0,
      },
      extras: {
        type: "array",
        title: t("extras"),
        items: {
          type: "object",
          properties: {
            extra_id: {
              type: "number",
              title: t("extra_id"),
            },
            price: {
              type: "number",
              title: t("extra_price"),
              min: 0,
              default: 0,
            },
            quantity: {
              type: "number",
              title: t("quantity"),
              min: 0,
              default: 1,
            },
          },
        },
      },
      checked_in: {
        type: "boolean",
        title: t("checked_in"),
        default: false,
      },
      checked_out: {
        type: "boolean",
        title: t("checked_out"),
        default: false,
      },
      total_price: {
        type: "number",
        title: t("total_price"),
        default: 0,
      },
      // Observation
      observation: {
        type: "string",
        title: t("observation"),
      },
    },
  },
  formWidgets: {
    "customer-selector": CustomerWithModalSelector,
    "pet-checkboxes-selector": PetCheckboxesSelector,
    days: DaysInput,
    "rooms-selector": RoomsSelector,
    "price-per-day": PricePerDaysInput,
    "extra-selector": ExtraSelector,
    "total-price-input": TotalPriceInput,
    "datetime-input": BeelditDateTimeInput,
  },
  uiFormSchema: {
    "ui:submitButtonOptions": {
      props: {
        disabled: false,
        className: "btn btn-info",
      },
      norender: true,
    },
    days: {
      "ui:widget": "days",
      "ui:options": {
        label: false,
      },
    },
    customer_id: {
      "ui:widget": "customer-selector",
    },
    pets: {
      "ui:widget": "pet-checkboxes-selector",
      "ui:options": {
        filters: {
          customer_id: "customer_id",
        },
      },
    },
    rooms: {
      "ui:widget": "rooms-selector",
    },
    price_per_day: {
      "ui:widget": "price-per-day",
    },
    total_price: {
      "ui:widget": "total-price-input",
    },
    observation: {
      "ui:widget": "textarea",
    },
    start_date_time: {
      "ui:widget": "datetime-input",
    },
    end_date_time: {
      "ui:widget": "datetime-input",
    },
    colsSchema: [
      // Parte izquierda con 4 columnas con customer y pet
      {
        cols: "col-4",
        elements: [
          [
            { name: "customer_id", cols: "col-12" },
            { name: "customer_info", cols: "col-12" },
            { name: "pets", cols: "col-12" },
          ],
        ],
      },
      {
        cols: "col-8",
        elements: [
          [
            { name: "start_date_time", cols: "col-3" },
            { name: "end_date_time", cols: "col-3" },
            { name: "days", cols: "col-3" },
          ],
          [
            { name: "rooms", cols: "col-3" },
            { name: "price_per_day", cols: "col-3" },
          ],
          [
            { name: "extras", cols: "col-12" },
            { name: "total_price", cols: "col-12" },
            { name: "observation", cols: "col-12" },
          ],
        ],
      },
    ],
    extras: {
      "ui:widget": "extra-selector",
      items: {
        colsSchema: [
          {
            cols: "col-12",
            elements: [
              [
                { name: "extra_id", cols: "col-4" },
                { name: "price", cols: "col-4" },
                { name: "quantity", cols: "col-4" },
              ],
            ],
          },
        ],
      },
    },
  },
};
export default bookingsCalendarConfig;
